import { useForm } from "react-hook-form";
import { FC, useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import SearchTable from "../../components/template/Search/SearchTable";
import { useFilter } from "../../hooks/filter/useFilter";
import { DOCUMENTS_SUMMARY, PAGE_SIZE_OPTIONS } from "../../utils/constants";
import { getProvince } from "../../services/TabelasAuxiliares/getProvince";
import { Option } from "../../types/General/Option";
import { FilterConfig } from "../../types/Components/Table/FilterConfig";
import { TableConfig } from "../../types/Components/Table/TableConfig";
import useFetchData from "../../hooks/data/useFetchData";
import useTableRendering from "../../hooks/table/useTableRendering";
import { PaginationConfig } from "../../types/Components/Table/PaginationConfig";
import { PaginationValues } from "../../types/General/PaginationValues";
import { getFiltersArray } from "../../helpers/getFiltersArray";
import { Dayjs } from "dayjs";
import { DocumentItemSchema } from "./data/DocumentItemSchema";
import { documentsSelectedFilteredData } from "./data/documentsSelectedFilteredData";
import TableDocuments from "./TableDocuments";
import { useCustomModal } from "../../hooks/modal/useCustomModal";
import { ModalConfig } from "../../types/Components/Modal/ModalConfig";
import ModalDocuments from "./component/ModalDocuments";
import { useAuth } from "../../provider/AuthProvider";
import { Box } from "@chakra-ui/react";
import { getDocuments } from "../../services/Documents/getDocuments";
import { DocumentType } from "../../types/Documents/DocumentType";
import { getSubSystem } from "../../services/Indicator/getSubSystem";
import { updateSelectedFilterItemsName } from "../../helpers/updateSelectedFilterItemsName";

interface FilterState {
  Year: Dayjs | null;
  ProvinceId: Option | null;
  SubSystemId: Option | null;
}

interface OptionsState {
  provinceOptions: Option[];
  subSystemOptions: Option[];
}

const Documents: FC = () => {
  const { auth } = useAuth();
  const user = auth.user;
  const [selectedFilterItems, setSelectedFilterItems] = useState<string>("");
  const [dataTable, setDataTable] = useState<DocumentType[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorFindingData, setErrorFindingData] = useState(false);
  const [selectedYearModal, setSelectedYearModal] = useState<Dayjs | null>(
    null,
  );
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [idItem, setIdItem] = useState<number>(0);
  const [uploadedFile, setUploadedFile] = useState<File | null | undefined>(
    undefined,
  );
  const [filterState, setFilterState] = useState<FilterState>({
    Year: null,
    ProvinceId: null,
    SubSystemId: null,
  });
  const [optionsState, setOptionsState] = useState<OptionsState>({
    provinceOptions: [] as Option[],
    subSystemOptions: [] as Option[],
  });
  const [shouldShowFileError, setShouldShowFileError] = useState(false);
  const [loadingFile, setLoadingFile] = useState(true);

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(DocumentItemSchema),
  });

  const filterNames = useMemo(
    () => ["ProvinceId", "SubSystemId", "PublishedAt", "DocumentOrAuthorName"],
    [],
  );
  const { filters, setFilter } = useFilter({ setValue, filterNames });

  const { isLoaded: provinceLoaded } = useFetchData(getProvince, (data) =>
    setOptionsState((prev) => ({ ...prev, provinceOptions: data })),
  );
  const { isLoaded: subSystemLoaded } = useFetchData(getSubSystem, (data) =>
    setOptionsState((prev) => ({ ...prev, subSystemOptions: data })),
  );

  useEffect(() => {
    const fetchData = (
      filterValues?: any,
      paginationValues?: PaginationValues,
    ) => {
      setErrorFindingData(false);
      setIsLoaded(false);
      setTimeout(() => {
        getDocuments(
          undefined,
          filterValues,
          paginationValues,
          user && user.permissions.includes("GetPrivateDocument"),
        )
          .then((result) => {
            setDataTable(result.content);
            setTotalItems(result.totalItems);
            setIsLoaded(true);
            setIsSearching(false);
          })
          .catch((error) => {
            console.log("API Error:", error);
            setErrorFindingData(true);
            setIsSearching(false);
          });
      }, 1000);
    };

    isSearching &&
      fetchData(filters, {
        CurrentPage: currentPage,
        PageSize: pageSize,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  useEffect(() => {
    const displayFormats = {
      Year: "YYYY",
    };
    updateSelectedFilterItemsName(
      filterState,
      filters,
      setSelectedFilterItems,
      filterNames,
      displayFormats,
    );
  }, [filters, filterState, filterNames]);

  const filtersArray = getFiltersArray(filterNames, filters, setFilter);

  const filterChildren = documentsSelectedFilteredData(
    optionsState.provinceOptions,
    optionsState.subSystemOptions,
    control,
    setFilter,
    (Year: any) => setFilterState((prev) => ({ ...prev, Year: Year })),
    (ProvinceId: any) =>
      setFilterState((prev) => ({ ...prev, ProvinceId: ProvinceId })),
    (SubSystemId: any) =>
      setFilterState((prev) => ({ ...prev, SubSystemId: SubSystemId })),
    filterState.Year,
    provinceLoaded,
    subSystemLoaded,
  );

  const tableConfig: TableConfig = {
    tableName: "Lista de documentos",
    dataTable,
    setDataTable,
    renderTable: (dataTable) => (
      <TableDocuments
        user={user}
        dataTable={dataTable}
        isLoaded={isLoaded}
        openModal={openModal}
        setValue={setValue}
        setIdUpdateItem={setIdItem}
        setUploadedFile={setUploadedFile}
        setLoadingFile={setLoadingFile}
      />
    ),
    isLoaded,
    errorFindingData,
    searchTitle: "Pesquisar pelo Nome ou Autor do documento",
  };

  const filterConfig: FilterConfig = {
    filterChildren,
    filters: filtersArray,
    selectedFilterItem: selectedFilterItems,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize, totalItems, setIsSearching);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
    totalItems,
  };

  const { isOpen, title, buttonTitle, openModal, closeModal } = useCustomModal(
    "Criar Documento",
    "Criar Documento",
  );

  const handleAddNewItem = () => {
    openModal("Criar Documento", "Criar Documento");
    setValue("name", "");
    setValue("authorName", "");
    setValue("provinceOptions", []);
    setValue("subSystemOptions", []);
    setValue("subSystemOptions", []);
    setValue("permission", null);
    setUploadedFile(undefined);
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
      setFilterState((prev) => ({ ...prev, ProvinceId: null }));
      setSelectedYearModal(null);
      setUploadedFile(undefined);
      setIsSearching(true);
      setIdItem(0);
      setShouldShowFileError(false);
      setLoadingFile(false);
    }
  }, [isOpen, reset, setSelectedYearModal]);

  const modalConfig: ModalConfig = {
    isOpen,
    title,
    buttonTitle,
    openModal,
    closeModal,
  };
  return (
    <Box paddingY={user ? 0 : 14} paddingX={user ? 0 : 24}>
      <SearchTable
        handleAddNewItem={
          user && user.permissions.includes("CreateDocument")
            ? handleAddNewItem
            : null
        }
        summaryName={user ? DOCUMENTS_SUMMARY : undefined}
        showModal
        searchTableName={user ? "Documentos" : "Repositório de Documentos"}
        tableConfig={tableConfig}
        filterConfig={filterConfig}
        paginationConfig={paginationConfig}
        setIsSearching={setIsSearching}
        setDates={[
          (Year: Dayjs | null) =>
            setFilterState((prev) => ({ ...prev, Year: Year })),
        ]}
      >
        <ModalDocuments
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          idItem={idItem ? idItem : 0}
          modalConfig={modalConfig}
          watch={watch}
          selectedYear={selectedYearModal}
          setSelectedYear={setSelectedYearModal}
          uploadedFile={uploadedFile}
          setUploadedFile={setUploadedFile}
          user={user}
          setShouldShowFileError={setShouldShowFileError}
          shouldShowFileError={shouldShowFileError}
          loadingFile={loadingFile}
        />
      </SearchTable>
    </Box>
  );
};

export default Documents;
